import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { IRowArr, IRows } from '@models/Forms/IForms';
import './SubForm.scss';
import Button from '@srm/ui/button';
import NestedFormBuilder from '@molecules/formbuilder/NestedFormBuilder/NestedFormBuilder';
import { ITableForm } from '@models/Forms/ITableForm';
import { Controller, useForm } from 'react-hook-form';
import { IField, IFieldElem } from '@models/IFormData';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import Modal from '@srm/ui/modal';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import { useChangeHandlersOptional } from '@controls/ChangeHandlers/useChangeHandlers';
import { convertListToMap } from '@utils/documentUtils';
import { IDocumentData } from '@models/document/IDocumentData';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { liveVariablesField } from '@molecules/formbuilder/utils/liveVariablesField';

export interface ISubFormProps {
    form: IRowArr;
    onClose: () => void;
    onSubmit?: (data: IField[]) => void;
    isEdit: boolean;
    children?: ReactNode;
    docId?: string;
    submitButtonCaption?: string;
    modalCaption?: string;
    formFields: IDocumentData;
    /** Переменные карточки: в значениях формы их нет, без них формулы и callAction подформы пусты. */
    variables?: IFieldElem;
}

const SubForm: React.FC<ISubFormProps> = ({
    form,
    onClose,
    onSubmit,
    isEdit,
    children,
    docId,
    submitButtonCaption,
    modalCaption,
    formFields,
    variables,
}: ISubFormProps) => {
    const parentChanges = useChangeHandlersOptional();
    const changeActions = useMemo(() => Array.from(parentChanges?.runtime.actions.values() ?? []), [parentChanges?.runtime]);
    const [rows, setRows] = useState<IRows>();
    const [fieldsData, setFieldsData] = useState<Record<string, IFieldElem>>();
    const methods = useForm<ITableForm>({
        mode: 'onBlur', // "onChange"
    });
    const disSubmit = React.useRef(false);
    // Escape и клик по фону закрывают окно: правки полей без вопроса не теряем
    const hasUnsavedInput = useUnsavedFormInput(methods);
    const variablesRef = useLatest(variables);

    useEffect(() => {
        const rows = { rows: form } as IRows;
        methods.reset(formFields);
        const fieldMap = convertListToMap(formFields?.fields);
        if (variablesRef.current) fieldMap.variables = liveVariablesField(() => variablesRef.current);
        setFieldsData(fieldMap);
        setRows(rows);
    }, [form]);

    const ModalFooter = ({ onClose }: { onClose: () => void }) => (
        <div className={'subFormModal_footer'}>
            {isEdit && (
                <Button type="submit" form="subForm" size="sm" disabled={disSubmit.current}>
                    {submitButtonCaption ? submitButtonCaption : 'Сохранить'}
                </Button>
            )}
            <Button onClick={onClose} variant="light" size="sm">
                {isEdit ? 'Отменить' : 'Ок'}
            </Button>
        </div>
    );

    const dirtyValues = (dirtyFields: any, allValues: ITableForm) => {
        return Object.keys(dirtyFields.fields).map((key) => allValues.fields[+key]);
    };

    const onSubmitHandler = (data: ITableForm) => {
        disSubmit.current = true;
        const dirtyFields = methods.formState.dirtyFields;
        const dirtyData = dirtyValues(dirtyFields, data);
        onSubmit && onSubmit(dirtyData);
    };

    return (
        <Modal
            size="xl"
            onClose={onClose}
            hasUnsavedInput={() => isEdit && hasUnsavedInput()}
            header={modalCaption ? modalCaption : 'Редактирование полей документа'}
            footer={<ModalFooter onClose={onClose} />}
        >
            <FormProviderControl
                id="subForm"
                changeActions={changeActions}
                formMethods={methods}
                className="sub-form"
                onSubmit={onSubmit ? onSubmitHandler : () => {}}
            >
                <HookFormProvider controller={Controller}>
                    <div className="sub-form__wrapper">
                        {children}

                        <div className="sub-form__content">
                            <NestedFormBuilder
                                fields={fieldsData!}
                                docId={docId}
                                setError={(errors?: string[]) => {
                                    if (errors) {
                                        sendModalMsg({
                                            message: errors,
                                            type: 'error',
                                        });
                                    }
                                }}
                                rows={rows}
                                isEdit={isEdit}
                                isNew={false}
                                formMethods={methods}
                                includedFields={[]}
                                uniqueKey={docId ? docId : '-1'}
                            />
                        </div>
                    </div>
                </HookFormProvider>
            </FormProviderControl>
        </Modal>
    );
};

export default SubForm;
